/**
 * Jain Gaurav Yatra — Walks CRUD API (guided-walk packages).
 * Backend: /jain_gaurav_yatra/walks/  (cookie-session auth + X-Tenant-ID).
 * JSON body (no file uploads), paginated list response.
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function authHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg =
    pick(d?.message) ??
    pick(d?.detail) ??
    (Array.isArray(d?.non_field_errors) ? pick((d.non_field_errors as string[])[0]) : null);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const WALKS_PATH = "/jain_gaurav_yatra/walks/";

/** Page-facing walk shape. `price` is normalised to a number; `is_free` is backend-derived. */
export interface Walk {
  id: string;
  badge: string;
  title: string;
  description: string;
  /** Where participants meet for this walk. */
  meet_up: string;
  price: number;
  is_free: boolean;
  features: string[];
  /** Walk includes breakfast. */
  is_breakfast: boolean;
  /** Walk includes a guide. */
  is_guide: boolean;
  is_active: boolean;
  created_at: string;
}

/** Fields accepted when creating/updating. `is_free`/`created_at` are read-only server-side. */
export interface WalkWrite {
  badge: string;
  title: string;
  description: string;
  /** Where participants meet for this walk. */
  meet_up: string;
  price: number;
  features: string[];
  is_breakfast: boolean;
  is_guide: boolean;
  is_active: boolean;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

function num(v: unknown): number {
  if (v === null || v === undefined || v === "") {return 0;}
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toFeatures(v: unknown): string[] {
  if (!Array.isArray(v)) {
    return [];
  }
  return v.map(str).filter((f) => f.trim());
}

function toWalk(row: Record<string, unknown>): Walk {
  const price = num(row.price);
  return {
    id: str(row.id),
    badge: str(row.badge),
    title: str(row.title),
    description: str(row.description),
    meet_up: str(row.meet_up ?? row.meetup_location ?? row.meeting_point),
    price,
    // Trust the server flag when present, else derive it.
    is_free: row.is_free !== undefined ? row.is_free === true : price === 0,
    features: toFeatures(row.features),
    is_breakfast: row.is_breakfast === true,
    is_guide: row.is_guide === true,
    is_active: row.is_active !== false,
    created_at: str(row.created_at),
  };
}

function toBody(p: WalkWrite): string {
  return JSON.stringify({
    badge: p.badge,
    title: p.title,
    description: p.description,
    meet_up: p.meet_up,
    price: p.price,
    features: p.features,
    is_breakfast: p.is_breakfast,
    is_guide: p.is_guide,
    is_active: p.is_active,
  });
}

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => ({}));
}

/** Unwrap either a bare array or a `{ results: [...] }` page. */
function rowsOf(data: unknown): Record<string, unknown>[] {
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return rows as Record<string, unknown>[];
}

export async function listWalks(): Promise<Walk[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${WALKS_PATH}`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return rowsOf(data).map(toWalk);
}

/**
 * Active walks for the public homepage. Never rejects — the landing page must not break if the
 * endpoint is unreachable for an anonymous visitor; it just renders nothing.
 */
export async function listActiveWalks(): Promise<Walk[]> {
  try {
    const all = await listWalks();
    return all.filter((w) => w.is_active);
  } catch {
    return [];
  }
}

export async function createWalk(payload: WalkWrite): Promise<Walk> {
  const base = apiBase();
  const res = await fetch(`${base}${WALKS_PATH}`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: toBody(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toWalk(data as Record<string, unknown>);
}

export async function updateWalk(id: string, payload: WalkWrite): Promise<Walk> {
  const base = apiBase();
  const res = await fetch(`${base}${WALKS_PATH}${id}/`, {
    method: "PATCH",
    headers: authHeaders(),
    credentials: "include",
    body: toBody(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toWalk(data as Record<string, unknown>);
}

export async function deleteWalk(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${WALKS_PATH}${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const data = await parseJson(res);
    throw new Error(errMessage(data, res.status));
  }
}
